/**
 * Extract session token from login-with-otp API response (shape may vary).
 */
export function extractOtpSessionToken(data) {
  if (!data || typeof data !== "object") return null;
  return (
    data.token ??
    data.data?.token ??
    data.session_token ??
    data.verification_token ??
    null
  );
}

/**
 * Normalize API error to a user-visible string.
 */
export function getApiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  if (!error) return fallback;
  const res = error.response;
  const d = res?.data;

  if (!d) {
    if (error.message && typeof error.message === "string") return error.message;
    return fallback;
  }

  if (typeof d === "string") return d;

  if (d.message != null) {
    return Array.isArray(d.message) ? d.message[0] : String(d.message);
  }
  if (d.detail != null) {
    return Array.isArray(d.detail) ? d.detail[0] : String(d.detail);
  }
  if (typeof d.error === "string") return d.error;
  if (Array.isArray(d.non_field_errors) && d.non_field_errors.length) {
    return String(d.non_field_errors[0]);
  }

  for (const key of Object.keys(d)) {
    const v = d[key];
    if (Array.isArray(v) && v.length) {
      return `${key}: ${v[0]}`;
    }
    if (typeof v === "string" && v) {
      return v;
    }
  }

  return fallback;
}

/** Decode JWT payload (browser-safe; no signature verify). */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function readAccessTokenFromStorage() {
  if (typeof localStorage === "undefined") return null;
  return (
    localStorage.getItem("access-token") ||
    localStorage.getItem("accessToken") ||
    null
  );
}

export function readJwtAuthFlags() {
  const payload = decodeJwtPayload(readAccessTokenFromStorage());
  if (!payload || typeof payload !== "object") {
    return {
      is_superuser: false,
      is_staff: false,
      is_hod: false,
      designation: "",
      reporting_role: "",
      user_type: "",
      role: "",
      dashboard: "",
    };
  }
  const role = String(payload.role || "").toLowerCase();
  const userType = String(payload.user_type || "").toLowerCase();
  return {
    is_superuser: payload.is_superuser === true || role === "superuser",
    is_staff:
      payload.is_staff === true ||
      payload.is_staff === 1 ||
      payload.is_staff === "true",
    is_hod:
      payload.is_hod === true ||
      role === "hod" ||
      userType.includes("hod"),
    designation: String(payload.designation || role || "").toLowerCase(),
    reporting_role:
      payload.reporting_role || (role === "hod" ? "hod" : ""),
    user_type: payload.user_type || "",
    role,
    dashboard: payload.dashboard || "",
  };
}

/** Superuser from Redux profile or JWT (SideNav uses the same merge). */
export function effectiveIsSuperuser(user) {
  const u = user || {};
  if (
    u.is_superuser === true ||
    u.is_superuser === 1 ||
    u.is_superuser === "true"
  ) {
    return true;
  }
  return readJwtAuthFlags().is_superuser;
}

/**
 * Full admin-panel route access: superuser or staff session on admin dashboard.
 * Matches tokens like `{ dashboard: "admin", is_staff: true }`.
 */
export function effectiveHasFullAdminRouteAccess(user) {
  if (effectiveIsSuperuser(user)) return true;
  const u = user || {};
  const jwt = readJwtAuthFlags();
  const staffFromProfile =
    u.is_staff === true || u.is_staff === 1 || u.is_staff === "true";
  const onAdminDashboard =
    String(jwt.dashboard || "").toLowerCase() === "admin";
  return onAdminDashboard && (staffFromProfile || jwt.is_staff);
}

/** HOD or superuser may assign sellers to team members. */
export function userCanManageEmployeeSellerAssignments(user) {
  const u = user || {};
  if (
    u.is_superuser === true ||
    u.is_superuser === 1 ||
    u.is_superuser === "true"
  ) {
    return true;
  }
  if (u.is_hod === true || u.is_hod === 1 || u.is_hod === "true") return true;
  if (String(u.reporting_role || "").toLowerCase() === "hod") return true;
  if (String(u.user_type || "").toLowerCase().includes("hod")) return true;

  const jwt = readJwtAuthFlags();
  if (jwt.is_superuser || jwt.is_hod) return true;
  if (String(jwt.reporting_role || "").toLowerCase() === "hod") return true;
  if (String(jwt.user_type || "").toLowerCase().includes("hod")) return true;
  return false;
}
